import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import Psp from "components/widget/Psp";
import { formatCurrency, longDateFormatStr } from "stores/JSHelper";
import { AmountCollectItem } from "stores/types";
import { FiExternalLink } from "react-icons/fi";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<AmountCollectItem>();

const columns = [
    columnHelper.accessor('createdOn', {
        id: 'date',
        header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Date</p>,
        cell: (info: any) => (
            <div className="flex items-center">
                <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">{longDateFormatStr(info.getValue())}</p>
            </div>
        ),
    }),
    columnHelper.accessor((row) => row, {
        id: 'partyName',
        header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Party</p>,
        cell: (info) => (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            <div>
                {info.getValue().partyName}
            </div>
            <div>
              <small>
                {info.getValue().partyId} | {info.getValue().updatedBy}
              </small>
            </div>
          </p>
        ),
      }),
    columnHelper.accessor('cashfreeReferenceId', {
        id: 'reference_id',
        header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Reference id</p>,
        cell: (info: any) => (
            <div className="flex items-center">
                <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">{info.getValue()}</p>
            </div>
        ),
    }),
    columnHelper.accessor('utr', {
        id: 'UTR',
        header: () => <p className="text-center text-sm font-bold text-gray-600 dark:text-white">UTR</p>,
        cell: (info) => <p className="text-center text-sm font-bold text-navy-700 dark:text-white">
          <div className='flex flex-row items-center justify-center'>
            <div className='mr-2' >
              <Psp empty pspType={info.row.original.pspType} />
            </div>
            {info.getValue()}
          </div>
        </p>,
      }),
      columnHelper.accessor('amount', {
        id: 'amount',
        header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white">Credit</p>,
        cell: (info) => (
          <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue() === 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
          </p>
        ),
      }),
      columnHelper.accessor('amountCollectedId', {
        id: 'amount_collected_id',
        header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white"></p>,
        cell: (info) => (
          <div className="flex flex-row justify-end pr-4">
            <Link to={`/admin/ops/easysplit/${info.getValue()}`} >
            <FiExternalLink className="h-4 w-4 text-gray-400 dark:text-white hover:cursor-pointer" color="blue"  />
            </Link>
          </div>
        ),
      }),
]

const AmountCollectedTransactionTable = ({ transactions }) => {
    const table = useReactTable({
        data: transactions,
        columns,
        enableSorting: false,
        state: {},
        onSortingChange: () => false,
        getCoreRowModel: getCoreRowModel(),
    });
    return (
        <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="w-full">
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} className="!border-px !border-gray-400">
                            {headerGroup.headers.map((header) => {
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        onClick={() => false}
                                        className="cursor-pointer border-b-[1px] border-gray-200 px-6 pb-2 pr-4 pt-4 text-start"
                                    >
                                        <div className="items-center justify-between text-xs text-gray-200">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {{
                                                asc: '',
                                                desc: '',
                                            }[header.column.getIsSorted() as string] ?? null}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                {table.getRowModel().rows.length > 0 && (
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <tr key={row.id} className="odd:bg-gray-50">
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td key={cell.id} className="min-w-[150px] border-white/0 px-2 py-3 pr-4">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                )}

                {table.getRowModel().rows.length === 0 && (
                    <tbody>
                        <tr>
                            <td colSpan={100} className="py-5 text-center">
                                No recent transactions
                            </td>
                        </tr>
                    </tbody>
                )}
            </table>
        </div>
    )
}

export default AmountCollectedTransactionTable;